import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { onboardingService } from '../services/onboardingService';
import { pageTipQueues, tipAliases } from '../constants/tipDefinitions';
import type { OnboardingContextValue } from '../types';

const OnboardingContext = createContext<OnboardingContextValue | undefined>(undefined);

/**
 * Onboarding Provider
 *
 * 提供全局的 Tips 狀態管理
 */
export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading: authLoading } = useAuth();
  // lazy initializer：第一次 render 時直接讀 localStorage
  // currentUser 來自 AuthContext（含快取），uid 已就緒可直接讀正確的 key
  // authLoading=true 時（無快取），uid 尚未確定，回傳 null 延遲顯示
  const [seenTips, setSeenTips] = useState<Set<string> | null>(() => {
    if (authLoading || !currentUser?.uid) return null;
    return new Set(onboardingService.getSeenTipsForUser(currentUser.uid));
  });
  // 頁面級延遲：key 為 pageId，value 為是否正在延遲
  const [pendingDelays, setPendingDelays] = useState<Record<string, boolean>>({});
  // 當前活躍的 Lesson Guide ID（null = 無活躍 lesson）
  // 使用 ref 而非 state，讓 getActiveTipForPage 始終讀到最新值，無需加入 deps
  const activeLessonGuideRef = useRef<string | null>(null);
  // LessonsPanel 是否可見（可見期間暫停一般 Tip，避免 z-index 衝突）
  const isLessonsPanelVisibleRef = useRef<boolean>(false);
  // Lesson 3 引導是否啟用（提升至 AppLayout，跨頁面持續存在）
  const [lesson3GuideState, setLesson3GuideState] = useState<boolean>(false);

  // auth 完成後才從 localStorage 載入，確保 userId 就緒再讀正確的 key
  // seenTips 為 null 時不顯示任何 tip，載入後才設為 Set
  useEffect(() => {
    if (authLoading) return;
    const loadedTips = onboardingService.getSeenTipsForUser(currentUser?.uid ?? '');
    setSeenTips(new Set(loadedTips));
  }, [authLoading, currentUser?.uid]);

  /**
   * 檢查是否應該顯示指定的 Tip
   */
  const shouldShowTip = useCallback((tipId: string) => {
    if (seenTips === null) return false;
    return !seenTips.has(tipId);
  }, [seenTips]);

  /**
   * 標記 Tip 為已看過
   */
  const markTipAsSeen = useCallback((tipId: string) => {
    // 先寫入 localStorage（側作用在 updater 外執行，避免 Strict Mode 雙重寫入）
    onboardingService.markTipAsSeen(tipId, currentUser?.uid);
    for (const alias of tipAliases[tipId] ?? []) {
      onboardingService.markTipAsSeen(alias, currentUser?.uid);
    }
    setSeenTips(prev => {
      const updated = new Set(prev ?? []);
      updated.add(tipId);
      for (const alias of tipAliases[tipId] ?? []) {
        updated.add(alias);
      }
      return updated;
    });
  }, [currentUser?.uid]);

  /**
   * 重設所有 Tips（開發/測試用）
   */
  const resetAllTips = useCallback(() => {
    setSeenTips(new Set<string>());
    onboardingService.resetAllTips(currentUser?.uid);
  }, [currentUser?.uid]);

  const setActiveLessonGuide = useCallback((lessonId: string | null) => {
    activeLessonGuideRef.current = lessonId;
  }, []);

  const getActiveLessonGuide = useCallback((): string | null => {
    return activeLessonGuideRef.current;
  }, []);

  const setLessonsPanelVisible = useCallback((visible: boolean) => {
    isLessonsPanelVisibleRef.current = visible;
  }, []);

  const startLesson3Guide = useCallback(() => {
    setLesson3GuideState(true);
  }, []);

  const closeLesson3Guide = useCallback(() => {
    setLesson3GuideState(false);
  }, []);

  /**
   * 取得指定頁面當前應顯示的 Tip ID（佇列中第一個未看過的）
   *
   * Lesson Guide 活躍期間回傳 null，避免一般 Tip 與教程引導競爭。
   * Lesson 自身的 Tip（如 lesson-2-ai-plan）由 LessonXGuide 直接渲染，
   * 不經過此 queue，因此不受此限制。
   */
  const getActiveTipForPage = useCallback((pageId: string): string | null => {
    // seenTips 尚未從 localStorage 載入完成，不顯示任何 tip
    if (seenTips === null) return null;
    // Lesson Guide 進行中，暫停所有一般 Tip
    if (activeLessonGuideRef.current !== null) return null;
    // LessonsPanel 可見期間，暫停一般 Tip（避免 z-index 穿透 Panel）
    if (isLessonsPanelVisibleRef.current) return null;

    // 該頁面正在延遲期間不顯示任何 Tip
    if (pendingDelays[pageId]) return null;

    const queue = pageTipQueues[pageId];
    if (!queue) return null;

    // 回傳佇列中第一個未看過的 Tip
    return queue.find(tipId => shouldShowTip(tipId)) ?? null;
  }, [seenTips, pendingDelays, shouldShowTip]);

  /**
   * 關閉當前 Tip 並延遲顯示下一個
   */
  const dismissAndShowNext = useCallback((tipId: string, pageId: string) => {
    // 立即標記為已看過
    markTipAsSeen(tipId);

    // 設定該頁面的延遲狀態
    setPendingDelays(prev => ({ ...prev, [pageId]: true }));

    // 800ms 後解除該頁面的延遲，觸發下一個 Tip 顯示
    const timerId = setTimeout(() => {
      setPendingDelays(prev => ({ ...prev, [pageId]: false }));
    }, 800);

    // Cleanup：頁面卸載時清除計時器
    return () => clearTimeout(timerId);
  }, [markTipAsSeen]);

  return (
    <OnboardingContext.Provider value={{ shouldShowTip, markTipAsSeen, resetAllTips, getActiveTipForPage, dismissAndShowNext, setActiveLessonGuide, getActiveLessonGuide, setLessonsPanelVisible, startLesson3Guide, closeLesson3Guide, lesson3GuideState }}>
      {children}
    </OnboardingContext.Provider>
  );
};

/**
 * useOnboarding Hook
 *
 * 取得 Onboarding Context
 * @throws 如果在 OnboardingProvider 外使用會拋出錯誤
 */
export const useOnboarding = (): OnboardingContextValue => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};
